import type { TournamentEvent, TournamentEventBody } from './events.js';
import { applyEvent, DomainError } from './reducer.js';
import type { Tournament } from './types.js';

/**
 * An in-memory event log with its folded state. Every change goes through
 * `dispatch`, which validates the event against the current state before
 * appending it. Undo removes the last event and re-folds.
 */
export class TournamentStore {
  #events: TournamentEvent[] = [];
  #state: Tournament | null = null;
  #listeners = new Set<(t: Tournament) => void>();

  constructor(
    events: readonly TournamentEvent[] = [],
    private readonly clock: () => string = () => new Date().toISOString(),
  ) {
    for (const e of events) {
      this.#state = applyEvent(this.#state, e);
      this.#events.push(e);
    }
  }

  get state(): Tournament {
    if (this.#state === null) throw new DomainError('tournament not created yet');
    return this.#state;
  }

  get hasState(): boolean {
    return this.#state !== null;
  }

  get events(): readonly TournamentEvent[] {
    return this.#events;
  }

  dispatch(body: TournamentEventBody, actor = 'local'): TournamentEvent {
    const next = applyEvent(this.#state, body);
    const event = {
      ...body,
      seq: this.#events.length + 1,
      at: this.clock(),
      actor,
    } as TournamentEvent;
    this.#events.push(event);
    this.#state = next;
    for (const l of this.#listeners) l(next);
    return event;
  }

  /** Remove the last event. Returns it, or null when the log holds only the creation. */
  undo(): TournamentEvent | null {
    if (this.#events.length <= 1) return null;
    const removed = this.#events.pop() as TournamentEvent;
    let s: Tournament | null = null;
    for (const e of this.#events) s = applyEvent(s, e);
    this.#state = s;
    if (s !== null) for (const l of this.#listeners) l(s);
    return removed;
  }

  subscribe(listener: (t: Tournament) => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }
}
